import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import ApiCallService from "../../Service/ApiCallService";
import {
  getPushStatus,
  subscribeUserToPush,
  unsubscribeUserFromPush,
} from "../../utils/registerPushNotifications";
import {
  NOTIFICATION_TYPES,
  NOTIFICATION_TYPE_CONFIG,
  NOTIFICATION_TYPE_LABELS,
} from "../../constants/notificationTypes";

const API_URL = process.env.REACT_APP_BASE_URL;
const DEFAULT_LIMIT = 20;

const getId = (notification) => String(notification?._id || notification?.id || "");

const getNotificationTime = (value) => {
  if (!value) return { relative: "Just now", exact: "" };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { relative: "Just now", exact: "" };

  const minutes = Math.max(0, Math.round((Date.now() - date.getTime()) / 60000));
  let relative = "Just now";
  if (minutes >= 1 && minutes < 60) relative = `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  else if (minutes >= 60 && minutes < 1440) {
    const hours = Math.round(minutes / 60);
    relative = `${hours} hour${hours === 1 ? "" : "s"} ago`;
  } else if (minutes >= 1440 && minutes < 10080) {
    const days = Math.round(minutes / 1440);
    relative = days === 1 ? "Yesterday" : `${days} days ago`;
  } else if (minutes >= 10080) relative = date.toLocaleDateString();

  return { relative, exact: date.toLocaleString() };
};

const getIcon = (type) => {
  switch (type) {
    case NOTIFICATION_TYPES.APPROVAL_GRANTED:
      return <CheckCircleOutlineIcon />;
    case NOTIFICATION_TYPES.APPROVAL_REJECTED:
    case NOTIFICATION_TYPES.SYSTEM_ALERT:
      return <ErrorOutlineOutlinedIcon />;
    case NOTIFICATION_TYPES.EVENT_REMINDER:
      return <EventAvailableOutlinedIcon />;
    case NOTIFICATION_TYPES.APPROVAL_REQUEST:
      return <InfoOutlinedIcon />;
    default:
      return <NotificationsNoneOutlinedIcon />;
  }
};

const getPayload = (response) => {
  const result = response || {};
  const data = Array.isArray(result.data)
    ? result.data
    : Array.isArray(result.notifications)
      ? result.notifications
      : [];
  const pagination = result.pagination || {};
  const total = Number(pagination.total ?? result.total ?? data.length);
  const totalPages = Number(
    pagination.totalPages ?? result.totalPages ?? Math.max(1, Math.ceil(total / (pagination.limit || DEFAULT_LIMIT)))
  );

  return {
    data,
    unreadCount: Number(result.unreadCount || 0),
    pagination: {
      page: Number(pagination.page || result.page || 1),
      limit: Number(pagination.limit || result.limit || DEFAULT_LIMIT),
      total,
      totalPages,
    },
  };
};

const applyNotificationFilters = (items, filters) => {
  const filtered = items.filter((notification) => {
    const matchesReadStatus =
      filters.isRead === "all" ||
      (filters.isRead === "read" ? notification.isRead === true : notification.isRead !== true);
    const matchesType = filters.type === "all" || notification.type === filters.type;
    return matchesReadStatus && matchesType;
  });

  return [...filtered].sort((first, second) => {
    if (filters.sort === "unread") {
      const unreadDifference = Number(second.isRead !== true) - Number(first.isRead !== true);
      if (unreadDifference !== 0) return unreadDifference;
    }

    const firstDate = new Date(first.createdAt || 0).getTime();
    const secondDate = new Date(second.createdAt || 0).getTime();
    return filters.sort === "oldest" ? firstDate - secondDate : secondDate - firstDate;
  });
};

const NotificationsPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("accessToken");
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pagination, setPagination] = useState({ page: 1, limit: DEFAULT_LIMIT, total: 0, totalPages: 1 });
  const [filters, setFilters] = useState({ isRead: "all", type: "all", sort: "newest" });
  const [loading, setLoading] = useState(Boolean(token));
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [pushState, setPushState] = useState({ loading: true, supported: true, permission: "default", isSubscribed: false });
  const [pushActionMsg, setPushActionMsg] = useState("");

  const refreshPushStatus = useCallback(async () => {
    setPushState((prev) => ({ ...prev, loading: true }));
    const status = await getPushStatus();
    setPushState({ ...status, loading: false });
  }, []);

  useEffect(() => {
    refreshPushStatus();
  }, [refreshPushStatus]);

  const handleTogglePush = async () => {
    setPushActionMsg("");
    setPushState((prev) => ({ ...prev, loading: true }));

    if (pushState.isSubscribed) {
      const res = await unsubscribeUserFromPush();
      if (res.success) {
        setPushActionMsg("Push notifications disabled.");
      } else {
        setPushActionMsg("Failed to disable push: " + (res.reason || "Error"));
      }
    } else {
      const res = await subscribeUserToPush();
      if (res.success) {
        setPushActionMsg("✅ Web Push Enabled! Background notifications active.");
      } else if (res.reason === "denied") {
        setPushActionMsg("❌ Notification permission was denied in browser settings.");
      } else if (res.reason === "not_supported") {
        setPushActionMsg("❌ Push notifications not supported in this browser.");
      } else {
        setPushActionMsg("Push setup failed: " + (res.reason || "Error"));
      }
    }
    await refreshPushStatus();
  };

  const fetchNotifications = useCallback(async () => {
    if (!token) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        sortBy: "createdAt",
        sortOrder: filters.sort === "oldest" ? "asc" : "desc",
      };
      if (filters.isRead !== "all") params.isRead = filters.isRead === "read";
      if (filters.type !== "all") params.type = filters.type;
      if (filters.sort === "unread") params.unreadFirst = true;

      const result = getPayload(await ApiCallService.get(`${API_URL}/notifications`, { params }));
      const visibleNotifications = applyNotificationFilters(result.data, filters);
      setNotifications(visibleNotifications);
      setUnreadCount(result.unreadCount);
      setPagination((previous) => ({
        ...previous,
        ...result.pagination,
        total: filters.isRead === "all" && filters.type === "all"
          ? result.pagination.total
          : visibleNotifications.length,
        totalPages: filters.isRead === "all" && filters.type === "all"
          ? result.pagination.totalPages
          : 1,
      }));
    } catch (err) {
      setError(err.response?.data?.error || "Failed to fetch notifications");
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit, pagination.page, token]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    const handleNewNotification = (event) => {
      const notification = event.detail;
      if (!notification || !getId(notification)) return;
      const matchesFilter =
        (filters.type === "all" || notification.type === filters.type) &&
        (filters.isRead === "all" || (filters.isRead === "read" ? notification.isRead : !notification.isRead));

      if (pagination.page === 1 && filters.sort === "newest" && matchesFilter) {
        setNotifications((previous) => {
          if (previous.some((item) => getId(item) === getId(notification))) return previous;
          return [notification, ...previous].slice(0, pagination.limit);
        });
        setPagination((previous) => ({ ...previous, total: previous.total + 1 }));
      }
      if (notification.isRead !== true) setUnreadCount((previous) => previous + 1);
    };

    const handleRead = (event) => {
      const id = String(event.detail?.id || "");
      if (!id) return;
      if (event.detail?.source === "page") return;
      setNotifications((previous) => {
        return previous.map((item) => {
          if (getId(item) !== id) return item;
          if (item.isRead !== true) {
            setUnreadCount((previousCount) => Math.max(0, previousCount - 1));
          }
          return { ...item, isRead: true };
        }).filter((item) => filters.isRead !== "unread" || item.isRead !== true);
      });
    };

    const handleReadAll = (event) => {
      if (event?.detail?.source === "page") return;
      setNotifications((previous) => filters.isRead === "unread"
        ? []
        : previous.map((item) => ({ ...item, isRead: true })));
      setUnreadCount(0);
    };

    window.addEventListener("bjs:notification:new", handleNewNotification);
    window.addEventListener("bjs:notification:read", handleRead);
    window.addEventListener("bjs:notification:read-all", handleReadAll);
    return () => {
      window.removeEventListener("bjs:notification:new", handleNewNotification);
      window.removeEventListener("bjs:notification:read", handleRead);
      window.removeEventListener("bjs:notification:read-all", handleReadAll);
    };
  }, [filters, pagination.limit, pagination.page]);

  const updateFilter = (key, value) => {
    setFilters((previous) => ({ ...previous, [key]: value }));
    setPagination((previous) => ({ ...previous, page: 1 }));
  };

  const markAsRead = async (notification) => {
    const id = getId(notification);
    if (!id || notification.isRead) return;
    setActionError("");
    setNotifications((previous) => previous
      .map((item) => (getId(item) === id ? { ...item, isRead: true } : item))
      .filter((item) => filters.isRead !== "unread" || item.isRead !== true));
    setUnreadCount((previous) => Math.max(0, previous - 1));
    try {
      await ApiCallService.patch(`${API_URL}/${id}/read`);
      window.dispatchEvent(new CustomEvent("bjs:notification:read", { detail: { id, source: "page" } }));
    } catch (err) {
      setNotifications((previous) => previous.map((item) => (getId(item) === id ? { ...item, isRead: false } : item)));
      setUnreadCount((previous) => previous + 1);
      setActionError(err.response?.data?.error || "Failed to mark notification as read");
    }
  };

  const markAllAsRead = async () => {
    if (!unreadCount) return;
    setActionError("");
    try {
      await ApiCallService.patch(`${API_URL}/read-all`);
      setNotifications((previous) => previous.map((item) => ({ ...item, isRead: true })));
      setUnreadCount(0);
      window.dispatchEvent(new CustomEvent("bjs:notification:read-all", { detail: { source: "page" } }));
    } catch (err) {
      setActionError(err.response?.data?.error || "Failed to mark all notifications as read");
    }
  };

  const handleNotificationClick = async (notification) => {
    await markAsRead(notification);
    if (notification.actionUrl && typeof notification.actionUrl === "string") {
      if (notification.actionUrl.startsWith("/")) navigate(notification.actionUrl);
      else window.location.assign(notification.actionUrl);
    }
  };

  const readCount = Math.max(0, pagination.total - unreadCount);
  const actionRequiredCount = notifications.filter((notification) =>
    [NOTIFICATION_TYPES.APPROVAL_REQUEST, NOTIFICATION_TYPES.APPROVAL_REJECTED, NOTIFICATION_TYPES.SYSTEM_ALERT].includes(notification.type)
  ).length;
  const weeklyActivity = Array.from({ length: 7 }, (_, index) => {
    const day = new Date();
    day.setHours(0, 0, 0, 0);
    day.setDate(day.getDate() - (6 - index));
    const nextDay = new Date(day);
    nextDay.setDate(nextDay.getDate() + 1);
    return {
      label: day.toLocaleDateString(undefined, { weekday: "short" }).slice(0, 1),
      count: notifications.filter((notification) => {
        const createdAt = new Date(notification.createdAt);
        return createdAt >= day && createdAt < nextDay;
      }).length,
    };
  });
  const maxWeeklyActivity = Math.max(1, ...weeklyActivity.map((item) => item.count));
  const approvalCount = notifications.filter((notification) => notification.type?.startsWith("APPROVAL_")).length;
  const securityCount = notifications.filter((notification) => notification.type === NOTIFICATION_TYPES.SYSTEM_ALERT).length;

  return (
    <Box sx={{
      minHeight: "calc(100vh - 60px)", background: "#f8fafc", px: { xs: 2, md: 3 }, py: { xs: 2.5, md: 3 },
      "@keyframes notificationEnter": {
        from: { opacity: 0, transform: "translateY(10px)" },
        to: { opacity: 1, transform: "translateY(0)" },
      },
      "@keyframes notificationPulse": {
        "0%, 100%": { boxShadow: "0 0 0 0 rgba(15,108,189,0.12)" },
        "50%": { boxShadow: "0 0 0 5px rgba(15,108,189,0)" },
      },
    }}>
      <Box sx={{ maxWidth: 1240, mx: "auto" }}>
        <Paper elevation={0} sx={{ p: { xs: 2, md: 2.25 }, mb: 2, border: "1px solid #e5eaf0", borderRadius: 2, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
          <Box>
            <Chip label="ACTIVITY CENTRE" size="small" sx={{ height: 20, mb: 0.5, fontSize: 9, fontWeight: 800, color: "#5264d9", bgcolor: "#eef0ff" }} />
            <Typography variant="h5" sx={{ fontWeight: 800, color: "#12314d", lineHeight: 1.2 }}>Notifications</Typography>
          </Box>
          <Button variant="contained" startIcon={<CheckCircleOutlineIcon />} onClick={markAllAsRead} disabled={!unreadCount || loading} sx={{ bgcolor: "#4054e8", borderRadius: 1.5, whiteSpace: "nowrap", "&:hover": { bgcolor: "#3042c9" } }}>
            Mark all as read
          </Button>
        </Paper>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" }, gap: 1.5, mb: 2 }}>
          {[
            { label: "Total notifications", value: pagination.total, color: "#4169e8", icon: <NotificationsNoneOutlinedIcon /> },
            { label: "Unread notifications", value: unreadCount, color: "#e89100", icon: <InfoOutlinedIcon /> },
            { label: "Read notifications", value: readCount, color: "#159c83", icon: <CheckCircleOutlineIcon /> },
            // { label: "Action required", value: actionRequiredCount, color: "#e52f55", icon: <WarningAmberRoundedIcon /> },
          ].map((stat, index) => (
            <Paper key={stat.label} elevation={0} sx={{ p: { xs: 1.25, md: 1.5 }, border: "1px solid #e5eaf0", borderRadius: 2, display: "flex", alignItems: "center", gap: 1.25, animation: "notificationEnter 420ms ease both", animationDelay: `${index * 80}ms`, position: "relative", overflow: "hidden", "&:after": { content: '""', position: "absolute", width: 80, height: 80, borderRadius: "50%", right: -35, top: -35, bgcolor: `${stat.color}10` } }}>
              <Box sx={{ width: 36, height: 36, borderRadius: 1.5, display: "grid", placeItems: "center", color: "#fff", bgcolor: stat.color, position: "relative", zIndex: 1 }}>{stat.icon}</Box>
              <Box sx={{ position: "relative", zIndex: 1 }}><Typography variant="h6" sx={{ fontWeight: 800, color: stat.color, lineHeight: 1.1 }}>{stat.value}</Typography><Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>{stat.label}</Typography></Box>
            </Paper>
          ))}
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.65fr) minmax(250px, 0.85fr)" }, gap: 2, alignItems: "start" }}>
          <Box>
        <Paper elevation={0} sx={{ p: { xs: 1.25, md: 1.5 }, mb: 1.5, border: "1px solid #e5eaf0", borderRadius: 2 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} flexWrap="wrap" alignItems={{ sm: "center" }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Read status</InputLabel>
              <Select value={filters.isRead} label="Read status" onChange={(event) => updateFilter("isRead", event.target.value)}>
                <MenuItem value="all">All</MenuItem><MenuItem value="unread">Unread</MenuItem><MenuItem value="read">Read</MenuItem>
              </Select>
            </FormControl>
            {/* <FormControl size="small" sx={{ minWidth: 190 }}>
              <InputLabel>Notification type</InputLabel>
              <Select value={filters.type} label="Notification type" onChange={(event) => updateFilter("type", event.target.value)}>
                <MenuItem value="all">All types</MenuItem>
                {Object.values(NOTIFICATION_TYPES).map((type) => <MenuItem key={type} value={type}>{NOTIFICATION_TYPE_LABELS[type]}</MenuItem>)}
              </Select>
            </FormControl> */}
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Sort by</InputLabel>
              <Select value={filters.sort} label="Sort by" onChange={(event) => updateFilter("sort", event.target.value)}>
                <MenuItem value="newest">Newest first</MenuItem><MenuItem value="oldest">Oldest first</MenuItem><MenuItem value="unread">Unread first</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Paper>

        {actionError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError("")}>{actionError}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={fetchNotifications}>Retry</Button>}>{error}</Alert>}

        <Paper elevation={0} sx={{ border: "1px solid #e5eaf0", borderRadius: 2, overflow: "hidden", animation: "notificationEnter 500ms ease both" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 9, px: 2 }}>
              <NotificationsNoneOutlinedIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
              <Typography variant="h6">No notifications found</Typography>
              <Typography variant="body2" color="text.secondary">Try changing your filters.</Typography>
            </Box>
          ) : (
            <List disablePadding>
              {notifications.map((notification, index) => {
                const id = getId(notification);
                const typeConfig = NOTIFICATION_TYPE_CONFIG[notification.type] || NOTIFICATION_TYPE_CONFIG[NOTIFICATION_TYPES.GENERAL];
                const isUnread = notification.isRead !== true;
                const time = getNotificationTime(notification.createdAt);
                return (
                  <ListItem key={id || `${notification.title}-${notification.createdAt}`} disablePadding divider sx={{ animation: "notificationEnter 380ms ease both", animationDelay: `${Math.min(index, 8) * 45}ms` }}>
                    <ListItemButton
                    //  onClick={() => handleNotificationClick(notification)}
                    
                    sx={{ alignItems: "flex-start", gap: 1.25, px: { xs: 1.5, md: 2 }, py: { xs: 1.4, md: 1.6 }, bgcolor: "#fff", "&:hover": { bgcolor: "#f8fbff" } }}>
                      <Box sx={{ width: 28, height: 28, borderRadius: 1, display: "grid", placeItems: "center", color: typeConfig.accent, bgcolor: `${typeConfig.accent}12`, flexShrink: 0, pt: 0, ...(isUnread ? { animation: "notificationPulse 2.2s ease-in-out infinite" } : {}) }}>{getIcon(notification.type)}</Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "flex-start" }} gap={0.5}>
                          <Typography variant="body2" sx={{ fontWeight: 800, color: "#183B56", overflowWrap: "anywhere" }}>{notification.title || typeConfig.label}{isUnread && <Box component="span" sx={{ display: "inline-block", width: 5, height: 5, borderRadius: "50%", bgcolor: "#159c83", ml: 0.75, verticalAlign: "middle" }} />}</Typography>
                          <Tooltip title={time.exact}>
                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>{time.relative}</Typography>
                          </Tooltip>
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, overflowWrap: "anywhere" }}>{notification.message || "No details available."}</Typography>
                        <Stack direction="row" spacing={0.75} sx={{ mt: 0.75 }} alignItems="center" flexWrap="wrap">
                          <Chip label={NOTIFICATION_TYPE_LABELS[notification.type] || typeConfig.label} size="small" sx={{ height: 20, fontSize: 10, color: typeConfig.accent, bgcolor: `${typeConfig.accent}12` }} />
                          {notification.actionUrl && <Typography variant="caption" sx={{ color: "#65728a", fontSize: 10 }}>· Action available</Typography>}
                        </Stack>
                      </Box>
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          )}
        </Paper>

          {!loading && (pagination.total > 0 || notifications.length > 0) && (
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary">Showing {notifications.length} of {pagination.total} notifications</Typography>
              <Stack direction="row" spacing={0.75}>
                <Button size="small" disabled={pagination.page <= 1} onClick={() => setPagination((previous) => ({ ...previous, page: previous.page - 1 }))}>Previous</Button>
                <Button size="small" variant="outlined" disabled={pagination.page >= pagination.totalPages} endIcon={<ArrowForwardIosRoundedIcon sx={{ fontSize: 11 }} />} onClick={() => setPagination((previous) => ({ ...previous, page: previous.page + 1 }))}>Next</Button>
              </Stack>
            </Stack>
          )}
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Paper elevation={0} sx={{ p: 2, border: "1px solid #e5eaf0", borderRadius: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 800, color: "#183B56", mb: 0.5 }}>
                Desktop & Mobile Push
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5, fontSize: 11, lineHeight: 1.4 }}>
                Receive background popups for journey approvals and profile updates even when the browser tab is idle.
              </Typography>

              {pushActionMsg && (
                <Alert severity={pushState.isSubscribed ? "success" : "info"} sx={{ mb: 1.5, py: 0.25, px: 1, fontSize: 11 }} onClose={() => setPushActionMsg("")}>
                  {pushActionMsg}
                </Alert>
              )}

              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Chip
                  label={pushState.isSubscribed ? "Push Active" : pushState.permission === "denied" ? "Blocked" : "Disabled"}
                  size="small"
                  color={pushState.isSubscribed ? "success" : pushState.permission === "denied" ? "error" : "default"}
                  sx={{ height: 22, fontSize: 10, fontWeight: 700 }}
                />
                <Button
                  size="small"
                  variant={pushState.isSubscribed ? "outlined" : "contained"}
                  color={pushState.isSubscribed ? "inherit" : "primary"}
                  onClick={handleTogglePush}
                  disabled={pushState.loading || !pushState.supported}
                  sx={{ borderRadius: 1.5, textTransform: "none", fontSize: 12, px: 1.5 }}
                >
                  {pushState.loading ? <CircularProgress size={16} /> : pushState.isSubscribed ? "Disable Push" : "Enable Push"}
                </Button>
              </Stack>
            </Paper>

            <Paper elevation={0} sx={{ display: { xs: "none", md: "block" }, p: 1.5, border: "1px solid #e5eaf0", borderRadius: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 800, color: "#183B56" }}>Weekly activity volume</Typography>
              <Chip label="Last 7 days" size="small" sx={{ height: 20, fontSize: 9, color: "#5264d9", bgcolor: "#eef0ff" }} />
            </Stack>
            <Box sx={{ height: 105, display: "flex", alignItems: "end", gap: 0.75, px: 0.5, borderBottom: "1px solid #edf0f5" }}>
              {weeklyActivity.map((item, index) => <Box key={`${item.label}-${index}`} sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}><Box sx={{ width: "100%", maxWidth: 26, height: `${Math.max(8, (item.count / maxWeeklyActivity) * 72)}px`, borderRadius: "4px 4px 0 0", bgcolor: index === 4 ? "#5964e8" : "#cbd5fa", transition: "height 400ms ease" }} /><Typography variant="caption" sx={{ fontSize: 9, color: "#9aa4b5" }}>{item.label}</Typography></Box>)}
            </Box>
            {/* <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Box sx={{ flex: 1, p: 1, bgcolor: "#f8fafc", borderRadius: 1.5 }}><Typography variant="caption" color="text.secondary">Approvals</Typography><Typography variant="body2" sx={{ fontWeight: 800, color: "#159c83" }}>{approvalCount}</Typography></Box>
              <Box sx={{ flex: 1, p: 1, bgcolor: "#f8fafc", borderRadius: 1.5 }}><Typography variant="caption" color="text.secondary">Security / Alert</Typography><Typography variant="body2" sx={{ fontWeight: 800, color: "#e52f55" }}>{securityCount}</Typography></Box>
            </Stack> */}
          </Paper>
          </Box>
        </Box>

      </Box>
    </Box>
  );
};

export default NotificationsPage;
